import { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import "./v1.css";
import {
  KPI_DEFS, parseNumber, calcChange, formatCompact, parseYearMonth,
  sortRowsByMonth, findRowForMonth, prevMonthOf, topChannels, findChannelRow, channelMixPercent, CHANNEL_NAME_KEY,
} from "./utils";
import { drawDonut, drawTrend, drawDualTrend } from "./charts";
import { buildEmailHtml, DEFAULT_BRANDING } from "./emailTemplate";
import StepUpload from "./steps/StepUpload";
import StepConfigure from "./steps/StepConfigure";
import StepPreview from "./steps/StepPreview";
import StepSend from "./steps/StepSend";

const STEPS = ["Upload", "Configure", "Preview", "Send"];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SiftraBadge = () => (
  <a
    href="https://siftra.co/"
    target="_blank"
    rel="noopener noreferrer"
    className="fixed bottom-4 right-4 z-50 flex items-center gap-2 pl-4 pr-3 py-2 rounded-full bg-white/95 backdrop-blur border border-[#e4e2da] shadow-[0_4px_16px_rgba(0,0,0,0.08)] hover:shadow-[0_6px_20px_rgba(0,0,0,0.14)] transition-shadow"
    data-testid="siftra-badge"
    aria-label="Built and Powered by Siftra"
  >
    <span className="text-[10px] font-semibold tracking-wider text-gray-400 uppercase whitespace-nowrap">Built & Powered by</span>
    <img src="/siftra-logo.webp" alt="Siftra" className="h-5 w-auto" />
  </a>
);


const LoginGate = ({ onLogin }) => {
  const [code, setCode] = useState("");
  const submit = () => {
    if (code === "ecom2026") {
      sessionStorage.setItem("v1_auth", "true");
      onLogin();
      toast.success("Welcome to the new Reports Hub");
    } else toast.error("Invalid passcode");
  };
  return (
    <div className="v1-root flex items-center justify-center min-h-screen px-4">
      <div className="v1-card p-10 w-full max-w-md text-center v1-fade-in" data-testid="v1-login-card">
        <div className="text-3xl font-extrabold tracking-tight">
          <span style={{ color: "#188038" }}>●</span> AGCO <span className="font-medium text-gray-500">Reports Hub</span>
        </div>
        <div className="mt-2 inline-block px-3 py-1 rounded-full text-xs font-bold" style={{ background: "#FEF7E0", color: "#B06000" }}>
          v1 — New Experience
        </div>
        <p className="text-sm text-gray-500 mt-4 mb-6">Enter your passcode to build this month's report.</p>
        <input
          type="password"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder="Passcode"
          data-testid="v1-login-passcode-input"
          className="w-full border border-gray-300 rounded-full px-5 py-3 text-center text-sm focus:outline-none focus:border-green-700"
        />
        <button onClick={submit} className="v1-btn-primary w-full mt-4" data-testid="v1-login-submit-button">
          Unlock
        </button>
      </div>
    </div>
  );
};

const Stepper = ({ step, maxStep, onGo }) => (
  <div className="flex items-center justify-center gap-2 sm:gap-3" data-testid="v1-stepper">
    {STEPS.map((label, i) => (
      <div key={label} className="flex items-center gap-2 sm:gap-3">
        <button
          onClick={() => i <= maxStep && onGo(i)}
          data-testid={`v1-step-${label.toLowerCase()}`}
          className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-bold transition-colors ${
            i === step ? "bg-[#188038] text-white" : i <= maxStep ? "bg-white border border-gray-300 text-gray-700 hover:border-green-700" : "bg-transparent text-gray-400 cursor-default"
          }`}
        >
          <span className={`w-5 h-5 rounded-full text-[11px] flex items-center justify-center ${i === step ? "bg-white/25" : i < step ? "bg-[#E6F4EA] text-[#137333]" : "bg-gray-200 text-gray-500"}`}>
            {i < step ? "✓" : i + 1}
          </span>
          {label}
        </button>
        {i < STEPS.length - 1 && <div className="w-4 sm:w-8 h-px bg-gray-300" />}
      </div>
    ))}
  </div>
);

export default function V1App() {
  const [authed, setAuthed] = useState(sessionStorage.getItem("v1_auth") === "true");
  const [step, setStep] = useState(0);
  const [maxStep, setMaxStep] = useState(0);
  const [files, setFiles] = useState([]); // {id, filename, type, rows, headers, months, period}
  const [config, setConfig] = useState({ month: "", year: "", country: "", currency: "EUR" });
  const [ai, setAi] = useState({ model: "claude", tone: "direct", length: "brief", skillName: "", instructions: "", findings: "", summaryContext: "", insightsContext: "", summary: "", insights: "" });
  const [includeCharts, setIncludeCharts] = useState(true);
  const [includeFindings, setIncludeFindings] = useState(true);
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [channelPrefs, setChannelPrefs] = useState({}); // { [originalName]: { included, alias } }
  const [skills, setSkills] = useState([]);

  // Load saved Skills (custom AI instructions from the original app) and preload the first one
  useEffect(() => {
    axios.get(`${API}/skills`).then((res) => {
      if (res.data?.success) {
        setSkills(res.data.skills);
        if (res.data.skills.length > 0) {
          const s = res.data.skills[0];
          setAi((prev) => (prev.instructions ? prev : { ...prev, skillName: s.name, instructions: s.instructions }));
        }
      }
    }).catch(() => {});
  }, []);

  // ---- Derived data ----
  const kpiRows = useMemo(() => {
    const byMonth = {};
    files.filter((f) => f.type === "kpi").forEach((f) =>
      f.rows.forEach((r) => { if (r["Year month"]) byMonth[r["Year month"]] = r; })
    );
    return sortRowsByMonth(Object.values(byMonth));
  }, [files]);

  const channelSets = useMemo(() => {
    const sets = { current: null, previous: null, lastYear: null };
    files.filter((f) => f.type === "channel").forEach((f) => { sets[f.period] = f.rows; });
    return sets;
  }, [files]);

  const kpiCurrent = useMemo(() => findRowForMonth(kpiRows, config.month, config.year), [kpiRows, config.month, config.year]);
  const kpiPrevious = useMemo(() => {
    const p = prevMonthOf(config.month, config.year);
    return findRowForMonth(kpiRows, p.month, p.year);
  }, [kpiRows, config.month, config.year]);
  const kpiLastYear = useMemo(
    () => findRowForMonth(kpiRows, config.month, String(parseInt(config.year || 0) - 1)),
    [kpiRows, config.month, config.year]
  );

  const kpis = useMemo(
    () =>
      KPI_DEFS.map((def) => {
        const val = kpiCurrent ? kpiCurrent[def.key] : null;
        return {
          label: def.label,
          key: def.key,
          display: formatCompact(val, { isCurrency: def.isCurrency, isPercent: def.isPercent, currency: config.currency }),
          raw: parseNumber(val),
          mom: kpiPrevious ? calcChange(val, kpiPrevious[def.key]) : null,
          yoy: kpiLastYear ? calcChange(val, kpiLastYear[def.key]) : null,
          lowerIsBetter: !!def.lowerIsBetter,
        };
      }),
    [kpiCurrent, kpiPrevious, kpiLastYear, config.currency]
  );

  // All channels in the current set (for the edit panel), sorted by revenue
  const allChannels = useMemo(() => {
    if (!channelSets.current) return [];
    return channelSets.current
      .map((r) => ({ name: (r[CHANNEL_NAME_KEY] || "").trim(), revenue: parseNumber(r["Total revenue"]) || 0 }))
      .filter((c) => c.name)
      .sort((a, b) => b.revenue - a.revenue);
  }, [channelSets]);

  const applyPrefs = (rows) =>
    (rows || []).filter((r) => channelPrefs[(r[CHANNEL_NAME_KEY] || "").trim()]?.included !== false);

  const channels = useMemo(() => {
    if (!channelSets.current) return [];
    const cur = applyPrefs(channelSets.current);
    const prev = channelSets.previous ? applyPrefs(channelSets.previous) : null;
    if (cur.length === 0) return [];
    return topChannels(cur, "Total revenue", 6).map((c) => {
      const prevRow = c.row ? findChannelRow(prev, c.name) : null;
      const mix = c.name === "Others" ? null : channelMixPercent(cur, c.name);
      const mixPrev = c.name === "Others" ? null : channelMixPercent(prev, c.name);
      return {
        name: channelPrefs[c.name]?.alias?.trim() || c.name,
        revenue: c.value,
        mix,
        mixShift: mix !== null && mixPrev !== null ? mix - mixPrev : null,
        sessions: c.row ? parseNumber(c.row["Sessions"]) : null,
        revMom: prevRow ? calcChange(c.value, prevRow["Total revenue"]) : null,
      };
    });
  }, [channelSets, channelPrefs]);

  // Monthly organic history (merged from 'organic' files; channel-history files filtered to Organic Search)
  const organicHistory = useMemo(() => {
    const byMonth = {};
    files.filter((f) => f.type === "organic").forEach((f) =>
      f.rows.forEach((r) => {
        if (!r["Year month"]) return;
        const ch = (r[CHANNEL_NAME_KEY] || "").trim().toLowerCase();
        if (ch && ch !== "organic search") return; // keep organic rows only
        byMonth[r["Year month"]] = r;
      })
    );
    return sortRowsByMonth(Object.values(byMonth));
  }, [files]);

  // Organic Search performance — prefers monthly history (up to 12 months), falls back to channel periods
  const organicData = useMemo(() => {
    const monthLabel = (r, i) => {
      const p = parseYearMonth(r["Year month"]);
      if (!p) return "";
      return i === 0 || p.month === "Jan" ? `${p.month} ${p.year.slice(2)}` : p.month;
    };
    if (organicHistory.length >= 2) {
      const hist = organicHistory.slice(-12);
      const cur = findRowForMonth(organicHistory, config.month, config.year) || hist[hist.length - 1];
      const prevP = prevMonthOf(config.month, config.year);
      const prev = findRowForMonth(organicHistory, prevP.month, prevP.year);
      return {
        revenue: parseNumber(cur["Total revenue"]),
        sessions: parseNumber(cur["Sessions"]),
        revMom: prev ? calcChange(cur["Total revenue"], prev["Total revenue"]) : null,
        sesMom: prev ? calcChange(cur["Sessions"], prev["Sessions"]) : null,
        points: hist.map((r, i) => ({ label: monthLabel(r, i), values: { revenue: parseNumber(r["Total revenue"]), sessions: parseNumber(r["Sessions"]) } })),
        monthsCount: hist.length,
      };
    }
    // Fallback: snapshot periods (last year → previous → current)
    const cur = findChannelRow(channelSets.current, "Organic Search");
    if (!cur) return null;
    const prevP = prevMonthOf(config.month, config.year);
    const prev = findChannelRow(channelSets.previous, "Organic Search");
    const ly = findChannelRow(channelSets.lastYear, "Organic Search");
    const points = [
      { label: `${config.month} ${parseInt(config.year || 0) - 1}`, row: ly },
      { label: `${prevP.month} ${prevP.year}`, row: prev },
      { label: `${config.month} ${config.year}`, row: cur },
    ]
      .filter((p) => p.row)
      .map((p) => ({ label: p.label, values: { revenue: parseNumber(p.row["Total revenue"]), sessions: parseNumber(p.row["Sessions"]) } }));
    return {
      revenue: parseNumber(cur["Total revenue"]),
      sessions: parseNumber(cur["Sessions"]),
      revMom: prev ? calcChange(cur["Total revenue"], prev["Total revenue"]) : null,
      sesMom: prev ? calcChange(cur["Sessions"], prev["Sessions"]) : null,
      points,
      monthsCount: points.length,
    };
  }, [organicHistory, channelSets, config.month, config.year]);

  // ---- Email HTML (single source of truth for preview + send + share) ----
  const buildHtml = (withCharts = includeCharts) => {
    let charts = null;
    if (withCharts) {
      const history = kpiRows.slice(-6);
      charts = {
        trend: drawTrend(history, "Total revenue", { title: "Revenue trend — last 6 months", isCurrency: true, currency: config.currency }),
        donut: channels.length
          ? drawDonut(channels.map((c) => ({ name: c.name, value: c.revenue })), { title: "Revenue by channel" })
          : null,
      };
    }
    const organic = organicData
      ? {
          ...organicData,
          chart:
            withCharts && organicData.points.length >= 2
              ? drawDualTrend(
                  organicData.points,
                  [
                    { key: "revenue", label: "Revenue", color: "#188038", isCurrency: true },
                    { key: "sessions", label: "Sessions", color: "#1A73E8" },
                  ],
                  { title: `Organic Search trend — last ${organicData.monthsCount} months`, currency: config.currency }
                )
              : null,
        }
      : null;
    return buildEmailHtml({
      config, kpis, channels, summary: ai.summary, insights: ai.insights, charts, branding, organic,
      findings: includeFindings && ai.findings.trim() ? ai.findings : "",
    });
  };

  const goTo = (i) => { setStep(i); setMaxStep((m) => Math.max(m, i)); window.scrollTo({ top: 0, behavior: "smooth" }); };

  if (!authed) return (
    <>
      <Toaster position="top-right" />
      <LoginGate onLogin={() => setAuthed(true)} />
      <SiftraBadge />
    </>
  );

  const stepProps = {
    files, setFiles, config, setConfig, kpiRows, kpis, channels, channelSets,
    kpiCurrent, kpiPrevious, kpiLastYear, ai, setAi, includeCharts, setIncludeCharts,
    branding, setBranding, allChannels, channelPrefs, setChannelPrefs, skills,
    includeFindings, setIncludeFindings,
    buildHtml, goTo, step,
  };

  return (
    <div className="v1-root">
      <Toaster position="top-right" />
      {/* Header */}
      <header className="border-b border-[#e4e2da] bg-white/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="text-lg font-extrabold tracking-tight" data-testid="v1-header-brand">
            <span style={{ color: "#188038" }}>●</span> AGCO <span className="font-medium text-gray-500">Reports Hub</span>
            <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold align-middle" style={{ background: "#FEF7E0", color: "#B06000" }}>v1</span>
          </div>
          <button
            onClick={() => { sessionStorage.removeItem("v1_auth"); setAuthed(false); }}
            className="text-xs font-semibold text-gray-500 hover:text-gray-800"
            data-testid="v1-logout-button"
          >
            Lock
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 pb-24">
        <Stepper step={step} maxStep={maxStep} onGo={goTo} />
        <div className="mt-8 v1-fade-in" key={step}>
          {step === 0 && <StepUpload {...stepProps} />}
          {step === 1 && <StepConfigure {...stepProps} />}
          {step === 2 && <StepPreview {...stepProps} />}
          {step === 3 && <StepSend {...stepProps} />}
        </div>
      </main>
      <SiftraBadge />
    </div>
  );
}
